import { useState } from 'react';

import styled from 'styled-components';

import { Button } from '@components/button';
import { Card } from '@components/card';
import { EmptyStateCell } from '@components/empty-state';
import { useConfirmModal } from '@components/generic-modals';
import { IconEmailAddress } from '@components/icons/email-address';
import { IconSearch } from '@components/icons/search';
import { RelativeSpinner } from '@components/loading-spinner';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';
import { abbreviateDate, parseDate } from '@utils/date';

import { useBlockedAddresses, useUnblockBouncedAddress } from 'api/company/hooks';
import { BlockedAddress, SuppressionCategory } from 'api/company/requests';

import { LoadFailedPanel, RetryingIndicator } from './load-failed-panel';

const ROWS_PER_STEP = 50;

type BlockedAddressSectionProps = {
  shortId: string;
  category: SuppressionCategory;
  title: string;
  helper: string;
  emptyText: string;
  search: string;
  canUnblock?: boolean;
};

export const BlockedAddressSection = ({
  shortId,
  category,
  title,
  helper,
  emptyText,
  search,
  canUnblock = false,
}: BlockedAddressSectionProps) => {
  const { t, lang } = useScopedTranslate(locale);
  const query = useBlockedAddresses({ shortId, category });
  const unblockMutation = useUnblockBouncedAddress(shortId);
  const showConfirmModal = useConfirmModal();

  const [visibleCount, setVisibleCount] = useState(ROWS_PER_STEP);
  const [visibleCountSearch, setVisibleCountSearch] = useState(search);
  if (search !== visibleCountSearch) {
    setVisibleCountSearch(search);
    setVisibleCount(ROWS_PER_STEP);
  }

  const term = search.trim().toLowerCase();
  const loadedAddresses = query.data?.items ?? [];
  const matchingAddresses = loadedAddresses.filter((item) =>
    item.address.toLowerCase().includes(term)
  );
  const dateLang = lang === 'is' ? 'is' : 'en';
  const visibleAddresses = matchingAddresses
    .slice(0, visibleCount)
    .map((item) => ({ ...item, displayText: describeEntry(item, dateLang) }));

  const handleUnblock = (address: BlockedAddress) => {
    showConfirmModal({
      title: t('unblockTitle'),
      description: t('unblockDescription', address.address),
      buttonText: t('unblock'),
      onConfirm: () => unblockMutation.mutateAsync({ shortId, address: address.address }),
    });
  };

  const handleRefetch = () => {
    query.refetch();
  };

  const handleShowMore = () => {
    setVisibleCount((count) => count + ROWS_PER_STEP);
  };

  const showFullError = !query.isLoading && query.isError && loadedAddresses.length === 0;
  const showList = !query.isLoading && !showFullError;

  return (
    <Stack space={4}>
      <Stack space={0}>
        <Text text={title} font="bold18" />
        <Text text={helper} font="regular14" color="textSecondary" />
      </Stack>

      {query.isLoading && <RelativeSpinner />}

      {showFullError && (
        <LoadFailedPanel
          title={t('errorHeader')}
          description={t('errorContent')}
          onRetry={handleRefetch}
          isRetrying={query.isFetching}
        />
      )}

      {showList && (
        <Stack space={2}>
          {matchingAddresses.length === 0 &&
            (term ? (
              <EmptyStateCell icon={<IconSearch />} text={t('noResults')} />
            ) : (
              <EmptyStateCell icon={<IconEmailAddress />} text={emptyText} />
            ))}

          {visibleAddresses.map((address) => (
            <Card key={address.address} spaceX={4} spaceY={3}>
              <Stack orientation="horizontal" justify="space-between" align="center">
                <TextColumn space={0}>
                  <Text text={address.address} font="semibold14" />
                  <Text
                    text={address.displayText}
                    font="regular14"
                    color="textSecondary"
                    ellipsisWithTooltip
                  />
                </TextColumn>
                {canUnblock && (
                  <UnblockButton
                    variant="ghost"
                    size="sm"
                    text={t('unblock')}
                    onClick={() => handleUnblock(address)}
                    disabled={
                      unblockMutation.isLoading &&
                      unblockMutation.variables?.address === address.address
                    }
                  />
                )}
              </Stack>
            </Card>
          ))}

          {matchingAddresses.length > visibleCount && (
            <Button variant="ghost" text={t('showMore')} onClick={handleShowMore} />
          )}

          {query.data?.isTruncated && (
            <Text text={t('truncated')} font="regular12" color="textHelper" />
          )}

          {query.isError && (
            <div role="status" aria-live="polite">
              <Stack orientation="horizontal" justify="space-between" align="center" space={2}>
                <TextColumn space={1}>
                  <Text text={t('refreshError')} font="regular12" color="textHelper" />
                </TextColumn>
                <Stack orientation="horizontal" space={3} align="center">
                  {query.isFetching && <RetryingIndicator />}
                  <UnblockButton
                    variant="secondary"
                    text={t('retry')}
                    onClick={handleRefetch}
                    disabled={query.isFetching}
                  />
                </Stack>
              </Stack>
            </div>
          )}
        </Stack>
      )}
    </Stack>
  );
};

const describeEntry = (address: BlockedAddress, lang: 'is' | 'en') => {
  const dateText = formatCreatedAt(address.createdAt, lang);
  const reasonText = [address.code, flattenSmtpReply(address.error)].filter(Boolean).join(' ');
  return [dateText, reasonText].filter(Boolean).join(' • ');
};

const SMTP_CONTINUATION_STATUS = /^\d\.\d{1,3}\.\d{1,3}\s+/;

const flattenSmtpReply = (reply: string | null) => {
  if (!reply) return '';
  return reply
    .split('\n')
    .map((line, index) => (index === 0 ? line : line.replace(SMTP_CONTINUATION_STATUS, '')))
    .join(' ')
    .replace(/\s+/g, ' ')
    .trim();
};

const formatCreatedAt = (date: string | null, lang: 'is' | 'en') => {
  if (!date) return '';
  if (Number.isNaN(parseDate(date).getTime())) return '';
  return abbreviateDate(date, lang, 'date');
};

const TextColumn = styled(Stack)({
  minWidth: 0,
});

const UnblockButton = styled(Button)({
  flexShrink: 0,
});

const locale = {
  en: {
    noResults: 'No results found',
    unblock: 'Unblock',
    unblockTitle: 'Unblock this address?',
    unblockDescription:
      'We will start sending to {0} again. If it bounces once more it is blocked automatically.',
    showMore: 'Show more',
    truncated: 'This list is too long to load in full, so search may miss some addresses.',
    errorHeader: 'Could not load blocked addresses',
    errorContent: 'Our email provider did not respond. Try again in a moment.',
    refreshError: 'Could not refresh the list. Some entries may be out of date.',
    retry: 'Try again',
  },
};
